import { buildApiUrl } from "../utils/apiConfig";

export type NcaaCourseRow = {
    id: string;
    icy: boolean;
    title: string;
    grade: string;
    credits: string;
};

export type NcaaSection = {
    id: string;
    rows: NcaaCourseRow[];
};

export type NcaaDivisionPayload = {
    sections: NcaaSection[];
};

export type NcaaTrackerPayload = {
    d1: NcaaDivisionPayload;
    d2: NcaaDivisionPayload;
};

export type NcaaTrackerResponseDto = {
    athleteProfileId: number;
    schemaVersion: number;
    payload: NcaaTrackerPayload | Record<string, unknown>;
    updatedAt: string | null;
};

function headersJson(): HeadersInit {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
    };
}

/** Athlete viewing self */
export async function fetchNcaaTrackerMe(): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl("/api/athletes/me/ncaa-tracker"), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
    });
    if (!res.ok) {
        throw new Error(`Failed to load NCAA tracker (${res.status})`);
    }
    return res.json();
}

export async function saveNcaaTrackerMe(payload: NcaaTrackerPayload, schemaVersion = 1): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl("/api/athletes/me/ncaa-tracker"), {
        method: "PUT",
        headers: headersJson(),
        credentials: "include",
        body: JSON.stringify({ payload, schemaVersion }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Save failed (${res.status})`);
    }
    return res.json();
}

/** Guardian or any client using /api/athletes/id/{profileId}/... */
export async function fetchNcaaTrackerByProfileId(profileId: number): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl(`/api/athletes/id/${encodeURIComponent(String(profileId))}/ncaa-tracker`), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
    });
    if (!res.ok) {
        throw new Error(`Failed to load NCAA tracker (${res.status})`);
    }
    return res.json();
}

export async function saveNcaaTrackerByProfileId(
    profileId: number,
    payload: NcaaTrackerPayload,
    schemaVersion = 1
): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl(`/api/athletes/id/${encodeURIComponent(String(profileId))}/ncaa-tracker`), {
        method: "PUT",
        headers: headersJson(),
        credentials: "include",
        body: JSON.stringify({ payload, schemaVersion }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Save failed (${res.status})`);
    }
    return res.json();
}

/** Staff admin viewing /athlete/ncaa/:athleteId */
export async function fetchNcaaTrackerForStaff(athleteProfileId: number): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl(`/api/athletes/${encodeURIComponent(String(athleteProfileId))}/ncaa-tracker`), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
    });
    if (!res.ok) {
        throw new Error(`Failed to load NCAA tracker (${res.status})`);
    }
    return res.json();
}

export async function saveNcaaTrackerForStaff(
    athleteProfileId: number,
    payload: NcaaTrackerPayload,
    schemaVersion = 1
): Promise<NcaaTrackerResponseDto> {
    const res = await fetch(buildApiUrl(`/api/athletes/${encodeURIComponent(String(athleteProfileId))}/ncaa-tracker`), {
        method: "PUT",
        headers: headersJson(),
        credentials: "include",
        body: JSON.stringify({ payload, schemaVersion }),
    });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Save failed (${res.status})`);
    }
    return res.json();
}
